import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { Copy, Check } from 'lucide-react';
import QuestionCreation from './QuestionCreation';
import './App.css';

const API_URL = process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:5000/api';
const FRONTEND_URL = process.env.NODE_ENV === 'production'
  ? 'https://questionary.votcode.com'
  : 'http://localhost:3000';

const MAX_QUESTION_LENGTH = 200;
const MAX_QUESTIONS = 20;

function App() {
  const [nickname, setNickname] = useState('');
  const [uniqueUrl, setUniqueUrl] = useState('');
  const [questions, setQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState('');
  const [answers, setAnswers] = useState([]);
  const [intersections, setIntersections] = useState([]);
  const [step, setStep] = useState('login');
  const [role, setRole] = useState('');
  const [intersectionStatus, setIntersectionStatus] = useState('loading');
  const [error, setError] = useState('');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [copied, setCopied] = useState(false);
  const [resetting, setResetting] = useState(false);

  const { id } = useParams();

  useEffect(() => {
    if (id) {
      setUniqueUrl(id);
      setStep('login');
    }
  }, [id]);

  const login = async () => {
    if (!nickname.trim()) {
      setError('Please enter a nickname');
      return;
    }
    try {
      const response = await axios.post(`${API_URL}/login`, { nickname, uniqueUrl: id || uniqueUrl });
      setUniqueUrl(response.data.uniqueUrl);
      setRole(response.data.role);
      setIsLoggedIn(true);
      
      if (response.data.questions && response.data.questions.length > 0) {
        setQuestions(response.data.questions);
        // If there are existing answers, use them; otherwise, set default 'Maybe' answers
        if (response.data.answers && response.data.answers.length > 0) {
          setAnswers(response.data.answers);
        } else {
          setAnswers(response.data.questions.map(q => ({ question: q, answer: 'Maybe' })));
        }
        setStep(response.data.role === 'creator' && response.data.answers.length === 0 ? 'createQuestionnaire' : 'answerQuestionnaire');
      } else if (response.data.role === 'creator') {
        setStep('createQuestionnaire');
      } else {
        setError('No questions available for this questionnaire.');
        setStep('login');
      }
      
      setError('');
    } catch (error) {
      console.error('Login failed:', error);
      setError(error.response?.data?.error || 'Login failed. Please try again.');
    }
  };

  const copyToClipboard = async () => {
    const fullUrl = `${FRONTEND_URL}/questionnaire/${uniqueUrl}`;
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000); // Reset after 2 seconds
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = fullUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const addQuestion = () => {
    if (currentQuestion.trim().length === 0) {
      setError('Question cannot be empty');
      return;
    }
    if (currentQuestion.length > MAX_QUESTION_LENGTH) {
      setError(`Question must be ${MAX_QUESTION_LENGTH} characters or less`);
      return;
    }
    if (questions.length >= MAX_QUESTIONS) {
      setError(`You can only add up to ${MAX_QUESTIONS} questions`);
      return;
    }
    setQuestions(prevQuestions => [...prevQuestions, currentQuestion]);
    setAnswers(prevAnswers => [...prevAnswers, { question: currentQuestion, answer: 'Maybe' }]);
    setCurrentQuestion('');
    setError('');
  };

  const createQuestionnaire = async () => {
    if (questions.length === 0) {
      setError('Please add at least one question');
      return;
    }
    try {
      await axios.post(`${API_URL}/questionnaire`, { uniqueUrl, questions });
      setStep('answerQuestionnaire');
      setError('');
    } catch (error) {
      console.error('Failed to create questionnaire:', error);
      setError('Failed to create questionnaire. Please try again.');
    }
  };

  const submitAnswers = async () => {
    try {
      const response = await axios.post(`${API_URL}/answers`, { uniqueUrl, nickname, answers });
      // Update the answers state with the response from the server
      setAnswers(response.data.answers || answers);
      setStep('showIntersections');
      setError('');
    } catch (error) {
      console.error('Failed to submit answers:', error);
      setError('Failed to submit answers. Please try again.');
    }
  };

  const fetchIntersections = async () => {
    try {
      const response = await axios.get(`${API_URL}/intersections/${uniqueUrl}`);
      if (response.data.status === 'waiting') {
        setIntersectionStatus('waiting');
        setIntersections([]);
      } else {
        setIntersectionStatus('complete');
        setIntersections(response.data.intersections || []);
      }
      setError('');
    } catch (error) {
      console.error('Failed to fetch intersections:', error);
      setIntersectionStatus('error');
      setError('Failed to fetch intersections. Please try again later.');
    }
  };

  useEffect(() => {
    if (step === 'showIntersections') {
      fetchIntersections();
    }
  }, [step]);

  // Separate useEffect for polling - stops when intersections are complete
  useEffect(() => {
    let intervalId;
    
    if (step === 'showIntersections' && (intersectionStatus === 'waiting' || intersectionStatus === 'loading')) {
      intervalId = setInterval(fetchIntersections, 5000);
    }
    
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [step, intersectionStatus]); // Polling depends on both step and status

  const resetPair = async () => {
    if (!window.confirm('Are you sure you want to reset the responder? This will allow a new person to join with the same link.')) {
      return;
    }
    setResetting(true);
    try {
      await axios.post(`${API_URL}/reset-pair`, { uniqueUrl, nickname });
      setIntersectionStatus('waiting');
      setIntersections([]);
      setError('');
      alert('Responder has been reset. You can now share the link again.');
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to reset responder.');
    } finally {
      setResetting(false);
    }
  };

  const renderUserAnswers = () => {
    return (
      <div className="user-answers">
        <h3>Your Answers</h3>
        {questions.length > 0 ? (
          <ul>
            {questions.map((question, index) => {
              const answer = answers.find(a => 
                a.question.type === question.type && 
                a.question.content === question.content &&
                a.question.text === question.text // Also match the optional text
              );
              return (
                <li key={index} className="flex flex-col items-center mb-4">
                  <div className="w-full max-w-2xl mx-auto">
                    <strong className="mb-4 block text-center">
                      {question.type === 'text' ? (
                        <span className="text-lg">{question.content}</span>
                      ) : (
                        <div>
                          {question.text && (
                            <p className="mb-4 text-lg font-medium">{question.text}</p>
                          )}
                          <img 
                            src={question.content} 
                            alt="Question" 
                            className="w-full h-auto object-contain max-h-96 rounded mb-4"
                          />
                        </div>
                      )}
                    </strong>
                    <div className="text-lg text-center">
                      {answer ? answer.answer : 'Maybe'}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        ) : (
          <p>No questions available.</p>
        )}
      </div>
    );
  };

  const renderStep = () => {
    if (!isLoggedIn) {
      return (
        <div className="login-form">
          <input
            type="text"
            value={nickname}
            onChange={(e) => setNickname(e.target.value)}
            placeholder="Enter nickname"
            maxLength="50"
          />
          <button onClick={login}>Login</button>
        </div>
      );
    }
    switch (step) {
      case 'createQuestionnaire':
        return (
          <div className="create-questionnaire">
            <QuestionCreation 
              onAddQuestion={(questionData) => {
                setQuestions(prevQuestions => [...prevQuestions, questionData]);
                setAnswers(prevAnswers => [
                  ...prevAnswers, 
                  { question: questionData, answer: 'Maybe' }
                ]);
              }}
              questionsCount={questions.length}
              maxLength={MAX_QUESTION_LENGTH}
              maxQuestions={MAX_QUESTIONS}
            />
            
            {questions.length > 0 && (
              <div className="mt-4">
                <h3 className="text-lg font-semibold mb-2">Added Questions:</h3>
                <ul className="space-y-4">
                  {questions.map((q, index) => (
                    <li key={index} className="p-4 bg-white rounded shadow">
                      {q.type === 'text' ? (
                        <p>{q.content}</p>
                      ) : (
                        <div className="flex flex-col items-center">
                          {q.text && (
                            <p className="mb-3 text-lg font-medium text-center">{q.text}</p>
                          )}
                          <img 
                            src={q.content} 
                            alt="Question" 
                            className="w-full h-auto object-contain max-h-96 rounded mb-4"
                          />
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            <button 
              onClick={createQuestionnaire} 
              disabled={questions.length === 0}
              className="w-full mt-4 px-4 py-2 bg-green-500 text-white rounded disabled:bg-gray-300"
            >
              Create Questionnaire
            </button>
          </div>
        );
        case 'answerQuestionnaire':
          return (
            <div className="answer-questionnaire">
              {questions.length > 0 ? (
                questions.map((q, index) => (
                  <div key={index} className="question mb-6 p-4 bg-white rounded shadow">
                    {q.type === 'text' ? (
                      <p className="mb-4 text-lg">{q.content}</p>
                    ) : (
                      <div className="flex flex-col items-center">
                        {q.text && (
                          <p className="mb-4 text-lg font-medium text-center">{q.text}</p>
                        )}
                        <img 
                          src={q.content} 
                          alt="Question" 
                          className="max-h-48 rounded mb-6"
                        />
                      </div>
                    )}
                    <div className="flex justify-center">
                      <select
                        value={answers.find(a => 
                          a.question.type === q.type && 
                          a.question.content === q.content &&
                          a.question.text === q.text
                        )?.answer || 'Maybe'}
                        onChange={(e) => setAnswers(prevAnswers => [
                          ...prevAnswers.filter(a => 
                            a.question.type !== q.type || 
                            a.question.content !== q.content ||
                            a.question.text !== q.text
                          ),
                          { question: q, answer: e.target.value }
                        ])}
                        className="w-1/3 p-2 border rounded text-center"
                      >
                        <option value="Yes">Yes</option>
                        <option value="No">No</option>
                        <option value="Maybe">Maybe</option>
                      </select>
                    </div>
                  </div>
                ))
              ) : (
                <p>No questions available. Please check with the questionnaire creator.</p>
              )}
              <button 
                onClick={submitAnswers} 
                disabled={questions.length === 0}
                className="w-full mt-4 px-4 py-2 bg-blue-500 text-white rounded disabled:bg-gray-300"
              >
                Submit Answers
              </button>
            </div>
          );
      case 'showIntersections':
        return (
          <div className="show-intersections">
            <h2 className="text-xl font-bold mb-4">Intersecting Answers</h2>
            
            {intersectionStatus === 'loading' && <p>Loading intersections...</p>}
            {intersectionStatus === 'waiting' && <p>Waiting for the other user to answer...</p>}
            {intersectionStatus === 'error' && <p>An error occurred while fetching intersections. Please try again later.</p>}
            
            {intersectionStatus === 'complete' && intersections.length === 0 && (
              <p>No matching answers found.</p>
            )}
            
            {intersectionStatus === 'complete' && intersections.length > 0 && (
              <ul className="space-y-4">
                {intersections.map((i, index) => {
                  const users = Object.keys(i).filter(key => key !== 'question');
                  return (
                    <li key={index} className="p-4 bg-white rounded shadow">
                      <div className="mb-3">
                        {i.question.type === 'text' ? (
                          <p className="font-semibold">{i.question.content}</p>
                        ) : (
                          <div className="flex flex-col items-center">
                            {i.question.text && (
                              <p className="font-semibold mb-4 text-lg text-center">{i.question.text}</p>
                            )}
                            <img 
                              src={i.question.content} 
                              alt="Question" 
                              className="w-full h-auto object-contain max-h-96 rounded mb-4"
                            />
                          </div>
                        )}
                      </div>
                      <div className="text-sm text-gray-600">
                        <p>{users[0]}: {i[users[0]]}</p>
                        <p>{users[1]}: {i[users[1]]}</p>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="App">
      <h1>Questionnaire App</h1>
      {isLoggedIn && nickname && <p className="user-info">Logged in as: {nickname} ({role})</p>}
      {isLoggedIn && uniqueUrl && (
        <div className="unique-url">
          <div className="flex">
            <span>Your unique URL:</span>
            <button
              onClick={copyToClipboard}
              className={`copy-button ${copied ? 'copied' : 'default'}`}
              title={copied ? 'Copied!' : 'Copy link'}
            >
              {copied ? (
                <>
                  <Check />
                  Copied!
                </>
              ) : (
                <>
                  <Copy />
                  Copy
                </>
              )}
            </button>
          </div>
          <a href={`${FRONTEND_URL}/questionnaire/${uniqueUrl}`}>
            {`${FRONTEND_URL}/questionnaire/${uniqueUrl}`}
          </a>
          {role === 'creator' && (
            <button
              onClick={resetPair}
              disabled={resetting}
              className="reset-button"
              title="Reset responder so a new person can join"
            >
              {resetting ? 'Resetting...' : 'Reset Responder'}
            </button>
          )}
        </div>
      )}
      {error && <p className="error">{error}</p>} 
      {renderStep()}
      {isLoggedIn && (step === 'showIntersections' || step === 'answerQuestionnaire') && renderUserAnswers()}
    </div>
  );
}

export default App;
